import { useState, useEffect, useMemo } from 'react';
import { history } from 'umi';
import { getHotTagsList, getQueryDefinition } from '@intelligence/services/common';

// ─── 类型定义 ─────────────────────────────────────────────────────────────────

/**
 * 热门标签选项数据结构
 * 由 hotTagsList 和 cond.intelligenceFilterTags 拼接而成
 */
export interface IHotTagOption {
    /** 标签名称（通用，接口原始字段） */
    name?: string;
    /** 标签中文名称 */
    name_cn?: string;
    /** 标签英文名称 */
    name_en?: string;
    /** 选项 hover 提示（中文） */
    hover_cn?: string;
    /** 选项 hover 提示（英文） */
    hover_en?: string;
    /** cond.intelligenceFilterTags 对应 key 的原始 value 对象 */
    value: any;
    /** value 的 JSON 序列化字符串，用于接口传参 */
    valueJson: string;
}

/**
 * hotTagsList 接口返回的单条数据结构
 */
interface IHotTagsListItem {
    /** 标签唯一名称，与 cond.intelligenceFilterTags 的 key 对应 */
    name: string;
    entity_type: string;
    description?: string;
    params?: string;
    name_en?: string;
    name_cn?: string;
    hover_en?: string;
    hover_cn?: string;
}

/**
 * @description 热门标签数据 Hook
 *
 * 数据来源：
 * 1. `/hotTagsList` 接口：提供标签展示信息（name、name_cn/en、hover_cn/en）
 * 2. `/cond` 接口（intelligenceFilterTags）：提供标签对应的筛选参数（value）
 *
 * 拼接逻辑：
 * 以 intelligenceFilterTags 的 key 为遍历基准，
 * 找到 hotTagsList 中 name === condKey 的条目进行合并，
 * 未匹配到的 key 会被过滤掉。
 *
 * @param entityType 实体类型（'mobile' | 'combine'），不传则不发起请求
 * @param url 指定页面 url；不传时自动从 window.location.href 提取 /v2 开头的路径
 */
const useHotTagsOptions = (entityType?: 'mobile' | 'combine', url?: string) => {
    // 解析最终使用的页面 url
    // 优先使用外部传入的 url，否则从当前页面 href 中提取 /v2 开头的路径（不含 query 参数）
    const resolvedUrl = useMemo(() => {
        if (url) return url;
        return window.location.href.match(/\/v2[^?]*/)?.[0] ?? '';
    }, [url]);

    const [hotTagsOptions, setHotTagsOptions] = useState<IHotTagOption[]>([]);
    // 请求加载状态：entityType 有值时初始为 true，请求完成后置 false
    const [hotTagsLoading, setHotTagsLoading] = useState<boolean>(!!entityType);

    useEffect(() => {
        // entityType 未传时不发起请求
        if (!entityType) return;

        setHotTagsLoading(true);
        const fetchData = async () => {
            try {
                // 并发请求两个接口，避免串行等待
                const [hotTagsRes, condRes] = await Promise.all([
                    // 接口1：获取热门标签展示信息列表
                    getHotTagsList(entityType),
                    // 接口2：获取当前页面的 intelligenceFilterTags（标签对应的筛选参数）
                    getQueryDefinition({
                        intelligenceFilterTags: {
                            target: 'intelligence_filter_tags',
                            params: {
                                page_url: [resolvedUrl],
                                params_type: ['page'],
                            },
                        },
                    }),
                ]);

                const hotTagsList: IHotTagsListItem[] = hotTagsRes?.data ?? [];
                const intelligenceFilterTags: Record<string, any> = condRes?.data?.intelligenceFilterTags ?? {};

                // 以 intelligenceFilterTags 的 key 为基准进行拼接
                // condKey 与 hotTagsList 中的 name 字段对应
                const options: IHotTagOption[] = Object.entries(intelligenceFilterTags)
                    .map(([condKey, condValue]) => {
                        const tagItem = hotTagsList.find((item) => item.name === condKey);
                        // 未在 hotTagsList 中找到对应条目则跳过
                        if (!tagItem) return null;

                        return {
                            name: tagItem.name,
                            name_cn: tagItem.name_cn,
                            name_en: tagItem.name_en,
                            hover_cn: tagItem.hover_cn,
                            hover_en: tagItem.hover_en,
                            // 原始 value 对象（供业务逻辑使用）
                            value: condValue,
                            // JSON 序列化后的 value（供接口传参使用）
                            valueJson: JSON.stringify(condValue),
                        };
                    })
                    .filter(Boolean) as IHotTagOption[];

                setHotTagsOptions(options);
            } catch (error) {
                console.error('[useHotTagsOptions] fetch failed:', error);
            } finally {
                setHotTagsLoading(false);
            }
        };

        fetchData();
    }, [entityType, resolvedUrl]);

    return {
        /** 当前页面解析后的 url */
        resolvedUrl,
        /** 热门标签选项列表，异步请求完成后更新 */
        hotTagsOptions,
        /** 请求加载状态 */
        hotTagsLoading,
        /** 当前 URL 的 filter_tag_name 参数，用于确定 HotTagsSource 的默认选中项 */
        filterTagName: (history.location.query?.filter_tag_name as string) ?? '',
    };
};

export default useHotTagsOptions;
